import { isSeriesPortFilled } from "./printerPortCheck";

/**
 * Pure decision for whether the printer form may be saved.
 *
 * ESS-3866: the save/edit handler in PrinterConfig.tsx used to gate the
 * series ("series") branch without requiring a port, so an empty COM port
 * slipped through. The gate is the same boolean for both the add and the
 * modify flow, so it lives here as one pure predicate that can be
 * unit-tested without mounting the full PrinterConfig component and its
 * store / SWR / i18n chain — mirroring computePrinterDuplicateIp and
 * isSeriesPortFilled.
 *
 * Keep the on-screen requirement (`inputPortSeries`) and this gate going
 * through isSeriesPortFilled so the two can't drift apart.
 */
export interface PrinterSaveGateInput {
    /** Printer transport — exactly one of the three flags is true. */
    isIpPrinter: boolean;
    isUsbPrinter: boolean;
    /** Code uniqueness / validity. */
    codeIsExist: boolean;
    isCodeValid: boolean;
    printerCode: string;
    /** Designation (printer name) uniqueness / presence. */
    designation: string;
    isNameExist: boolean;
    /** Ticket type selection (null when nothing is picked yet). */
    tickets: unknown;
    /** IP-printer fields. */
    ip: string;
    isIpExist: boolean;
    ipPortNumber: string;
    /** Series-printer COM port. */
    portSeries: string | null | undefined;
    /** Validation patterns (injected so the rule stays pure / testable). */
    ipMatchWord: RegExp;
    numberMatchWord: RegExp;
}

const EMPTY_VALUE = "";

export function canSavePrinter(input: PrinterSaveGateInput): boolean {
    const {
        isIpPrinter,
        isUsbPrinter,
        codeIsExist,
        isCodeValid,
        printerCode,
        designation,
        isNameExist,
        tickets,
        ip,
        isIpExist,
        ipPortNumber,
        portSeries,
        ipMatchWord,
        numberMatchWord,
    } = input;

    const commonValid =
        isCodeValid &&
        !codeIsExist &&
        !isNameExist &&
        designation.length !== 0 &&
        printerCode !== EMPTY_VALUE &&
        tickets !== null;

    if (isIpPrinter) {
        // The port is mandatory only for the series type. For IP printers the
        // network port is optional; when one IS provided it must be numeric.
        const ipPortValid =
            ipPortNumber.length === 0 || numberMatchWord.test(ipPortNumber);
        return (
            commonValid &&
            !isIpExist &&
            ip.length !== 0 &&
            ipMatchWord.test(ip) &&
            ipPortValid
        );
    }

    if (isUsbPrinter) {
        return commonValid;
    }

    // Series printer: must carry a non-empty COM port (ESS-3866).
    return commonValid && !isIpExist && isSeriesPortFilled(portSeries);
}
